class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    const view = this.dataset.view || 'modal';
    const sectionUrl = (() => {
      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('view', view);
        return url.toString();
      } catch (e) {
        const base = this.dataset.productUrl || '';
        const joinChar = base.includes('?') ? '&' : '?';
        return `${base}${joinChar}view=${encodeURIComponent(view)}`;
      }
    })();

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 200);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
          syncVariantPickerFromUrl(drawerContent, this.dataset.productUrl || '');
          selectFirstAvailableVariantIfSoldOut(drawerContent);
          filterQuickViewGalleryByColor(drawerContent);
          syncQuickViewColorHeading(drawerContent);
          scheduleQuickViewAddToBagSync(drawerContent);
          drawerContent.querySelectorAll('variant-radios input[type="radio"]').forEach(function (input) {
            input.addEventListener('change', function () {
              selectFirstAvailableVariantIfSoldOut(drawerContent);
              filterQuickViewGalleryByColor(drawerContent);
              syncQuickViewColorHeading(drawerContent);
              scheduleQuickViewAddToBagSync(drawerContent);
            });
          });

          bindQuickViewModalSyncEvents();
        }, 500);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);

function getVariantPickerData(container) {
  const variantRadios = container?.querySelector('variant-radios');
  const script = variantRadios?.querySelector('script[type="application/json"]');
  if (!script) return null;
  try {
    const variants = JSON.parse(script.textContent || '[]');
    const fieldsets = Array.from(variantRadios.querySelectorAll('.variant-input-wrapper'));
    return Array.isArray(variants) && variants.length && fieldsets.length ? { variants, fieldsets } : null;
  } catch (_) {
    return null;
  }
}

function selectedOptionsFrom(fieldsets) {
  return fieldsets.map(fs => fs.querySelector('input[type="radio"]:checked')?.value ?? null);
}

function findVariantByOptions(variants, options) {
  return variants.find(v => Array.isArray(v?.options) && v.options.every((opt, i) => opt === options[i]));
}

function applyVariantToRadios(container, variant) {
  const data = getVariantPickerData(container);
  if (!data || !variant?.options) return;
  let lastInput = null;
  data.fieldsets.forEach((fieldset, i) => {
    const val = variant.options[i];
    const input = val != null && Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(inp => inp.value === val);
    if (input && !input.checked) {
      input.checked = true;
      lastInput = input;
    }
  });
  lastInput?.dispatchEvent(new Event('change', { bubbles: true }));
}

function selectFirstAvailableVariantIfSoldOut(container) {
  const data = getVariantPickerData(container);
  if (!data) return;
  const { variants, fieldsets } = data;
  const opts = selectedOptionsFrom(fieldsets);
  const current = findVariantByOptions(variants, opts);
  if (!current || current.available) return;
  const n = opts.length;
  const first = variants.find(v => v.available && (n <= 1 || opts.slice(0, n - 1).every((o, i) => v.options[i] === o)))
    || variants.find(v => v.available);
  if (first) applyVariantToRadios(container, first);
}

function syncVariantPickerFromUrl(container, productUrl) {
  const variantId = productUrl?.match(/[?&]variant=(\d+)/)?.[1];
  if (!container || !variantId) return;
  const data = getVariantPickerData(container);
  if (!data) return;
  const variant = data.variants.find(v => v.id === parseInt(variantId, 10));
  if (variant?.options) applyVariantToRadios(container, variant);
  selectFirstAvailableVariantIfSoldOut(container);
}

/**
 * Filter Quick View gallery to show only images matching the selected color.
 * Uses alt text markers (#colour_X or #color_X). Safe: if no matches, shows all images.
 */
function filterQuickViewGalleryByColor(container) {
  if (!container) return;

  const variantRadios = container.querySelector('variant-radios');
  const colorWrapper = variantRadios?.querySelector('[data-option-slug="colour"], [data-option-slug="color"]');
  const checkedColorInput = colorWrapper?.querySelector('input[type="radio"]:checked') || variantRadios?.querySelector('input[type="radio"]:checked');
  const selectedColor = checkedColorInput?.value?.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '');

  const mediaItems = container.querySelectorAll('.product__media-item');
  if (!mediaItems.length) return;

  const variantMarkers = selectedColor ? [
    '#colour_' + selectedColor,
    '#color_' + selectedColor,
    '#' + selectedColor,
    'colour_' + selectedColor,
    'color_' + selectedColor
  ] : [];

  let anyShown = false;

  mediaItems.forEach(function (item) {
    const img = item.querySelector('img');
    const altText = (img?.getAttribute('alt') || '').toLowerCase().replace(/color_/g, 'colour_');

    if (!variantMarkers.length || !altText.includes('#')) {
      item.style.display = '';
      anyShown = true;
      return;
    }

    const matches = variantMarkers.some(function (m) {
      return altText.includes(m.toLowerCase());
    });

    if (matches) {
      item.style.display = '';
      anyShown = true;
    } else {
      item.style.display = 'none';
    }
  });

  if (!anyShown && mediaItems.length) {
    mediaItems.forEach(function (item) {
      item.style.display = '';
    });
  }

  const firstVisible = Array.from(mediaItems).find(function (el) {
    return el.style.display !== 'none';
  });
  if (firstVisible) {
    mediaItems.forEach(function (el) {
      el.classList.toggle('is-active', el === firstVisible);
    });
  }
}

function syncQuickViewColorHeading(container) {
  if (!container) return;

  const label = container.querySelector('[id^="QuickViewColorLabel-"]');
  const variantRadios = container.querySelector('variant-radios');
  if (!label || !variantRadios) return;

  const colorWrapper = variantRadios.querySelector('[data-option-slug="colour"], [data-option-slug="color"]');
  const checkedColor = colorWrapper?.querySelector('input[type="radio"]:checked') || null;
  if (!checkedColor || !checkedColor.value) return;

  label.textContent = checkedColor.value;
  label.classList.remove('hidden');
}

function syncQuickViewAddToBag(container) {
  if (!container?.closest('quick-view.quick-view[data-view="modal-new"]')) return;
  const button = container.querySelector('.product-form__submit-new');
  const variantInput = container.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  if (!button || !variantInput) return;
  const data = getVariantPickerData(container);
  if (!data) return;
  const opts = selectedOptionsFrom(data.fieldsets);
  if (!opts.length || opts.some(o => o == null)) return;
  const variant = findVariantByOptions(data.variants, opts);
  if (!variant) return;
  variantInput.value = variant.id;
  variantInput.disabled = false;
  if (variant.available) {
    button.disabled = false;
    button.textContent = 'ADD TO BAG - ' + formatQuickViewMoney(variant.price);
  } else {
    button.disabled = true;
    button.textContent = 'SOLD OUT';
  }
}

function scheduleQuickViewAddToBagSync(container) {
  if (!container) return;

  syncQuickViewAddToBag(container);
  [60, 180, 360].forEach(function (delay) {
    window.setTimeout(function () {
      syncQuickViewAddToBag(container);
    }, delay);
  });
}

function syncAllOpenModalNewQuickViews() {
  document.querySelectorAll('quick-view.quick-view[data-view="modal-new"] .quick-view__content.hide-cover').forEach(function (content) {
    syncQuickViewColorHeading(content);
    scheduleQuickViewAddToBagSync(content);
  });
}

function bindQuickViewModalSyncEvents() {
  if (window.__quickViewModalNewSyncBound) return;
  window.__quickViewModalNewSyncBound = true;

  document.addEventListener('variant:change', function () {
    syncAllOpenModalNewQuickViews();
  });

  document.addEventListener('productInfo:loaded', function () {
    syncAllOpenModalNewQuickViews();
  });
}

function formatQuickViewMoney(cents) {
  const moneyFormat = window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount_no_decimals}}';
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents, moneyFormat).replace('Rs.', '₹').replace('INR', '₹');
  }

  const value = Number(cents || 0) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value);
}
